import { addDays, formatIsoDate, parseIsoDate } from './dates.js';
import { PROJECT_CATEGORIES } from './rules.js';

/**
 * Lot AY « Sous-traitance » (décisions de Maxime du 19/10/2026, maquette `docs/maquette/sous-traitance-1910.html`).
 * Logique pure partagée par le serveur (métriques, trésorerie, fiche sous-traitant) et le front (bloc de saisie).
 *
 * - Un devis signé peut confier des LIGNES précises à des sous-traitants (sous-traitant + coût payé, prérempli avec
 *   le prix de la ligne), ou un MONTANT GLOBAL (sous-traitant + montant), ou « Aucune ». La part d'Evolusite = montant
 *   du devis − sous-traité (jamais négative : le sous-traité est plafonné au montant du devis).
 * - Une ligne sous-traitée sort de son produit à hauteur de son coût (la marge reste dans le produit) ; le montant
 *   global, l'excédent d'un coût sur le prix de sa ligne et les lignes devenues introuvables sont retirés au prorata.
 * - Facturé et encaissé restent BRUTS (factures réelles) ; seuls les chiffres « signé / vendu » passent en part
 *   d'Evolusite.
 */

/** Délai de paiement des sous-traitants par défaut (Réglages > Chiffres) : 3 mois après le début du projet. */
export const DEFAULT_SUBCONTRACT_PAYMENT_DELAY_MONTHS = 3;
/** Comptes comptables de la sous-traitance (poste `sous-traitance`, `expenses/categories.ts`). */
export const SUBCONTRACTING_ACCOUNT_PREFIXES: readonly string[] = ['604', '611'];
/** Une dépense d'un sous-traitant peut précéder de quelques jours la signature du devis qu'elle paie. */
export const SUBCONTRACT_ATTACH_LOOKBACK_DAYS = 3;
/** « À vérifier » (reprise) : dépense 604/611 entre 15 jours avant et 90 jours après la signature. */
export const SUBCONTRACT_HINT_WINDOW = { before: 15, after: 90 } as const;

// ------------------------------------------------------------------ Identité d'une ligne de devis

/** Texte comparable d'une ligne (minuscules, sans accents ni ponctuation). */
function normalizeLineText(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Empreinte FNV-1a sur 32 bits, en hexadécimal (8 caractères) : stable, sans dépendance au navigateur. */
function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Empreinte d'une ligne de devis : produit et libellé (`quotes.lines[].product` / `label`, soit `product_name` et
 * `title` d'Axonaut). L'API Axonaut ne donne pas d'identifiant de ligne : la clé d'une ligne est son rang + cette
 * empreinte (un prix modifié garde la ligne ; un libellé ou un produit changé la rend introuvable).
 */
export function quoteLineFingerprint(line: { product?: string | null; label?: string | null }): string {
  return fnv1a(`${normalizeLineText(line.product)}|${normalizeLineText(line.label)}`);
}

/** Clé stable d'une ligne : `<rang>:<empreinte>` (« 1:9f2c4a01 »). */
export function quoteLineKey(
  index: number,
  line: { product?: string | null; label?: string | null },
): string {
  return `${index}:${quoteLineFingerprint(line)}`;
}

export function parseQuoteLineKey(key: string): { index: number; fingerprint: string } | null {
  const match = /^(\d{1,4}):([0-9a-f]{8})$/.exec(key);
  return match ? { index: Number(match[1]), fingerprint: match[2]! } : null;
}

/**
 * Retrouve chaque ligne sous-traitée enregistrée dans les lignes ACTUELLES du devis (le devis a pu changer dans
 * Axonaut) : même clé (même rang, même empreinte), sinon même empreinte à un autre rang (ligne déplacée, la première
 * libre). Une ligne retrouvée nulle part est « introuvable » : son coût reste compté (l'engagement envers le
 * sous-traitant ne disparaît pas), retiré au prorata, et l'écran la signale.
 */
export function resolveLineItems<T extends { lineKey: string }>(
  lines: readonly { product?: string | null; label?: string | null }[],
  items: readonly T[],
): { matched: (T & { index: number; moved: boolean })[]; orphans: T[] } {
  const prints = lines.map((l) => quoteLineFingerprint(l));
  const used = new Set<number>();
  const matched: (T & { index: number; moved: boolean })[] = [];
  const pending: T[] = [];
  for (const item of items) {
    const parsed = parseQuoteLineKey(item.lineKey);
    if (parsed && prints[parsed.index] === parsed.fingerprint && !used.has(parsed.index)) {
      used.add(parsed.index);
      matched.push({ ...item, index: parsed.index, moved: false });
    } else pending.push(item);
  }
  const orphans: T[] = [];
  for (const item of pending) {
    const parsed = parseQuoteLineKey(item.lineKey);
    const index = parsed ? prints.findIndex((p, i) => p === parsed.fingerprint && !used.has(i)) : -1;
    if (index >= 0) {
      used.add(index);
      matched.push({ ...item, index, moved: true });
    } else orphans.push(item);
  }
  matched.sort((a, b) => a.index - b.index);
  return { matched, orphans };
}

// ------------------------------------------------------------------ Part d'Evolusite

/** Sous-traitance d'un devis pour les calculs : coût par rang de ligne, et ce qui est retiré au prorata. */
export interface SubcontractAmounts {
  /** Lignes sous-traitées (rang dans `quotes.lines`) et coût payé au sous-traitant. */
  lineCosts: readonly { index: number; costCents: number }[];
  /** Montant global + lignes introuvables : retirés au prorata des lignes. */
  proratedCents: number;
}

export const NO_SUBCONTRACTING: SubcontractAmounts = { lineCosts: [], proratedCents: 0 };

/** Coût total confié (non plafonné). */
export function subcontractCostCents(sub: SubcontractAmounts | null | undefined): number {
  if (!sub) return 0;
  return sub.lineCosts.reduce((s, l) => s + Math.max(0, l.costCents), 0) + Math.max(0, sub.proratedCents);
}

/** Sous-traité d'un devis, plafonné à son montant (la part d'Evolusite n'est jamais négative). */
export function subcontractedCents(amountCents: number, sub: SubcontractAmounts | null | undefined): number {
  if (amountCents <= 0) return 0;
  return Math.min(amountCents, subcontractCostCents(sub));
}

/** Part d'Evolusite d'un devis : montant − sous-traité. */
export function evolusiteShareCents(amountCents: number, sub: SubcontractAmounts | null | undefined): number {
  return amountCents - subcontractedCents(amountCents, sub);
}

/** Répartit `total` au prorata des poids positifs (plus forts restes), somme exacte. */
function prorata(total: number, weights: readonly number[]): number[] {
  const sumWeights = weights.reduce((s, w) => s + (w > 0 ? w : 0), 0);
  if (total <= 0 || sumWeights <= 0) return weights.map(() => 0);
  const parts = weights.map((w, index) => {
    const exact = w > 0 ? (total * w) / sumWeights : 0;
    return { index, value: Math.floor(exact), rest: exact - Math.floor(exact) };
  });
  let remaining = total - parts.reduce((s, p) => s + p.value, 0);
  for (const part of [...parts].sort((a, b) => b.rest - a.rest || a.index - b.index)) {
    if (remaining <= 0) break;
    if ((weights[part.index] ?? 0) <= 0) continue;
    part.value += 1;
    remaining -= 1;
  }
  return parts.map((p) => p.value);
}

/**
 * Montants nets (part d'Evolusite) de morceaux d'un devis (`pieces[i]` = montant de la ligne de rang
 * `indexes[i]`) : chaque ligne sous-traitée perd son coût, au plus son montant (la marge reste dans le produit) ;
 * l'excédent d'un coût, le montant global et les lignes introuvables sont retirés au prorata de ce qui reste, sans
 * descendre sous zéro. Somme = somme des morceaux − sous-traité plafonné.
 */
export function netPieceAmounts(
  pieces: readonly number[],
  sub: SubcontractAmounts | null | undefined,
  indexes: readonly number[] = pieces.map((_, i) => i),
): number[] {
  if (!sub || (sub.lineCosts.length === 0 && sub.proratedCents <= 0)) return [...pieces];
  const costByIndex = new Map<number, number>();
  for (const line of sub.lineCosts) {
    costByIndex.set(line.index, (costByIndex.get(line.index) ?? 0) + Math.max(0, line.costCents));
  }
  let excess = Math.max(0, sub.proratedCents);
  const net = pieces.map((amount, i) => {
    const cost = costByIndex.get(indexes[i]!) ?? 0;
    if (cost === 0) return amount;
    const removed = Math.min(cost, Math.max(0, amount));
    excess += cost - removed;
    return amount - removed;
  });
  // Coûts de lignes absentes des morceaux (ligne sans montant) : retirés au prorata eux aussi.
  const present = new Set(indexes);
  for (const [index, cost] of costByIndex) if (!present.has(index)) excess += cost;
  const available = net.reduce((s, v) => s + Math.max(0, v), 0);
  const deductions = prorata(Math.min(excess, available), net);
  return net.map((v, i) => v - (deductions[i] ?? 0));
}

// ------------------------------------------------------------------ Lignes de maquette

const MOCKUP_TEXT = /maquette|webdesign|web design|direction artistique|\bdesign\b|\bui\b|\bux\b/;

/**
 * Ligne « de maquette » : catégorie de création de site (vitrine, e-commerce, refonte : celles qui ont une maquette
 * au planning, `PROJECT_CATEGORIES`), ou libellé qui parle de maquette / design. La sous-traiter coche « Maquette
 * sous-traitée » d'office dans « Préparer le projet » (booléen du planning `projects.subcontracted`, inchangé).
 */
export function isMockupLine(line: {
  category?: string | null;
  label?: string | null;
  product?: string | null;
}): boolean {
  if (line.category && PROJECT_CATEGORIES.has(line.category)) return true;
  return MOCKUP_TEXT.test(normalizeLineText(`${line.product ?? ''} ${line.label ?? ''}`));
}

// ------------------------------------------------------------------ Date « au plus tard »

/** `date` + `months` mois (le jour est ramené au dernier jour du mois s'il n'existe pas : 31/01 + 1 → 28/02). */
export function addMonthsToDate(date: string, months: number): string {
  const { year, month, day } = parseIsoDate(date);
  const index = year * 12 + (month - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return formatIsoDate({ year: y, month: m, day: Math.min(day, last) });
}

export type SubcontractStartSource = 'project' | 'signature' | 'none';

/**
 * Date « à payer au plus tard » d'un devis sous-traité (décision 7) : début du projet (première étape du planning du
 * projet relié au devis) + délai des Réglages (3 mois par défaut) ; sans projet, la date de signature ; la date
 * choisie à la main sur le devis (« Changer la date ») gagne.
 */
export function subcontractDueDate(input: {
  projectStart: string | null;
  signedOn: string | null;
  delayMonths: number;
  override?: string | null;
}): { date: string | null; start: string | null; startSource: SubcontractStartSource; overridden: boolean } {
  const start = input.projectStart ?? input.signedOn;
  const startSource: SubcontractStartSource = input.projectStart
    ? 'project'
    : input.signedOn
      ? 'signature'
      : 'none';
  if (input.override) return { date: input.override, start, startSource, overridden: true };
  return {
    date: start ? addMonthsToDate(start, Math.max(0, Math.round(input.delayMonths))) : null,
    start,
    startSource,
    overridden: false,
  };
}

/** Début d'un projet : la plus ancienne date de ses étapes (`phases`), null sans date. */
export function projectStartOf(
  phases: Record<string, string | null | undefined> | null | undefined,
): string | null {
  const dates = Object.values(phases ?? {}).filter(
    (d): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d),
  );
  return dates.length ? [...dates].sort()[0]! : null;
}

// ------------------------------------------------------------------ Qui est sous-traitant

/**
 * Est sous-traitant (décision 4) : un fournisseur Axonaut à qui on a confié au moins un devis dans le Cockpit, ou
 * qui a une dépense en compte 604 / 611 (poste « Sous-traitance ») ; le classement à la main (oui / non) gagne.
 */
export function isSubcontractor(
  rule: { hasQuote: boolean; hasExpense: boolean },
  override: 'yes' | 'no' | null | undefined,
): boolean {
  if (override === 'yes') return true;
  if (override === 'no') return false;
  return rule.hasQuote || rule.hasExpense;
}

/** Compte comptable de sous-traitance (604…, 611…). */
export function isSubcontractingAccount(code: string | null | undefined): boolean {
  const normalized = (code ?? '').replace(/\s+/g, '');
  return SUBCONTRACTING_ACCOUNT_PREFIXES.some((p) => normalized.startsWith(p));
}

// ------------------------------------------------------------------ Rattachement des dépenses

export interface AttachCommitment {
  quoteId: string;
  supplierId: string;
  /** Date de signature du devis (`AAAA-MM-JJ`), null si inconnue. */
  signedOn: string | null;
  costCents: number;
}

export interface AttachExpense {
  id: string;
  supplierId: string;
  date: string;
  amountCents: number;
  /** Choix à la main (« Changer », « Rattacher ») : un devis, ou « aucun devis ». Il gagne et survit aux synchros. */
  manual: { kind: 'quote'; quoteId: string } | { kind: 'none' } | null;
}

export interface Attachment {
  quoteId: string | null;
  how: 'auto' | 'user';
}

/**
 * Rattachement des dépenses d'un sous-traitant à ses devis (décision 8). Le choix à la main gagne. Sinon, dans
 * l'ordre des dates : le plus ancien devis de ce sous-traitant signé à la date de la dépense (3 jours de marge) et
 * encore ouvert, c'est-à-dire dont les factures déjà rattachées ne couvrent pas le coût confié. C'est un CALCUL
 * affiché (rien n'est enregistré), corrigeable par « Changer ».
 */
export function attachExpenses(
  commitments: readonly AttachCommitment[],
  expenses: readonly AttachExpense[],
): Map<string, Attachment> {
  const key = (quoteId: string, supplierId: string) => `${quoteId}|${supplierId}`;
  const cost = new Map<string, number>();
  for (const c of commitments)
    cost.set(key(c.quoteId, c.supplierId), (cost.get(key(c.quoteId, c.supplierId)) ?? 0) + c.costCents);
  const filled = new Map<string, number>();
  const out = new Map<string, Attachment>();
  for (const e of expenses) {
    if (!e.manual) continue;
    if (e.manual.kind === 'none') {
      out.set(e.id, { quoteId: null, how: 'user' });
      continue;
    }
    out.set(e.id, { quoteId: e.manual.quoteId, how: 'user' });
    const k = key(e.manual.quoteId, e.supplierId);
    filled.set(k, (filled.get(k) ?? 0) + e.amountCents);
  }
  const bySupplier = new Map<string, AttachCommitment[]>();
  for (const c of commitments) {
    const list = bySupplier.get(c.supplierId) ?? [];
    if (!list.some((x) => x.quoteId === c.quoteId)) list.push(c);
    bySupplier.set(c.supplierId, list);
  }
  for (const list of bySupplier.values()) {
    list.sort(
      (a, b) =>
        (a.signedOn ?? '9999').localeCompare(b.signedOn ?? '9999') || a.quoteId.localeCompare(b.quoteId),
    );
  }
  const autos = expenses
    .filter((e) => !e.manual)
    .sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  for (const e of autos) {
    const limit = addDays(e.date, SUBCONTRACT_ATTACH_LOOKBACK_DAYS);
    const target = (bySupplier.get(e.supplierId) ?? []).find((c) => {
      if (c.signedOn === null || c.signedOn > limit) return false;
      const k = key(c.quoteId, c.supplierId);
      return (filled.get(k) ?? 0) < (cost.get(k) ?? 0);
    });
    if (!target) {
      out.set(e.id, { quoteId: null, how: 'auto' });
      continue;
    }
    const k = key(target.quoteId, target.supplierId);
    filled.set(k, (filled.get(k) ?? 0) + e.amountCents);
    out.set(e.id, { quoteId: target.quoteId, how: 'auto' });
  }
  return out;
}

/**
 * Reste à payer d'un engagement (devis × sous-traitant) : coût confié (ou ce qu'il a facturé, si c'est plus) − ce
 * qui est déjà payé de ses factures rattachées. Une facture reçue mais pas payée reste dans le reste à payer (une
 * seule fois : elle n'est jamais comptée ailleurs dans la trésorerie).
 */
export function commitmentRemainingCents(input: {
  costCents: number;
  invoicedCents: number;
  paidCents: number;
}): number {
  return Math.max(0, Math.max(input.costCents, input.invoicedCents) - input.paidCents);
}
